import { createSlice } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

/**

* M_State slice
* ---
* Generic UI / navigation state used by the application (tabs, in-page memory,
* small key/value flags, etc.).
*
* IMPORTANT:
* * This slice is intentionally domain-agnostic (no user/carrier logic inside).
* * Realm isolation is handled at the persistence layer (see factory below).
*/
export const stateSlice = createSlice({
    name: "M_State",
    initialState: {
        // Array of { key, value } entries used by tabs and UI helpers
        StateKeyValue: [],

        // Simple counter to track how many keys are currently stored
        StateQuantity: 0,
    },
    reducers: {
        /**
      
        * Adds or updates a key/value entry.
        * * If the key already exists, its value is replaced.
        * * Otherwise, a new entry is added and the quantity is incremented.
            */
        addM_State(state, action) {
            const tempObj = state.StateKeyValue.find(
                (obj) => obj.key === action.payload.key
            );

            if (tempObj) {
                state.StateKeyValue = state.StateKeyValue.map((obj) => {
                    if (obj.key === action.payload.key) {
                        return { key: obj.key, value: action.payload.value };
                    }
                    return obj;
                });
            } else {
                state.StateKeyValue.push({
                    key: action.payload.key,
                    value: action.payload.value,
                });
                state.StateQuantity++;
            }
        },

        /**
      
        * Removes a key/value entry if it exists.
        * Decrements the quantity counter accordingly.
          */
        removeM_State(state, action) {
            const tempObj = state.StateKeyValue.find(
                (obj) => obj.key === action.payload.key
            );

            if (tempObj) {
                state.StateKeyValue = state.StateKeyValue.filter(
                    (obj) => obj.key !== action.payload.key
                );
                state.StateQuantity--;
            }
        },

        /**
      
        * Clears all stored UI state.
        * Used mainly on logout or hard UI resets.
          */
        DeleteAll_M_State(state) {
            state.StateKeyValue = [];
            state.StateQuantity = 0;
        },
    },
});

/**

* Realm-scoped persisted reducer factory.
*
* PURPOSE:
* * The application runs multiple realms ("user" and "carrier") under the same
* browser origin.
* * localStorage is shared across tabs and realms.
* * Using a single redux-persist key would cause UI state (tabs, selections)
* to leak between realms.
*
* WHAT THIS DOES:
* * Creates a persisted reducer using a realm-specific storage key.
*
* RESULTING KEYS:
* user    -> "persist:M_State_user"
* carrier -> "persist:M_State_carrier"
*
* This guarantees full isolation of UI/tab state between realms.
  */
export function createPersistedReducerForRealm(
    realm /* 'user' | 'carrier' */
) {
    // Defensive normalization: default to "user"
    const safeRealm = realm === "carrier" ? "carrier" : "user";

    const persistConfig = {
        key: `M_State_${safeRealm}`,
        storage,
    };

    return persistReducer(persistConfig, stateSlice.reducer);
}

// Export actions for use across the app
export const M_StateActions = stateSlice.actions;
